import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Activity, Scan, HeartPulse, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { labInfo, services, heroImages } from '../mockData';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Advanced Medical Diagnostics in Trivandrum
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                {labInfo.fullName} offers state-of-the-art diagnostic services with accurate results and professional care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${labInfo.phone}`}>
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
                <a href={labInfo.googleMapsLink} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-blue-600 text-blue-600 hover:bg-blue-50">
                    <MapPin className="w-5 h-5 mr-2" />
                    Get Directions
                  </Button>
                </a>
              </div>
              
              {/* Quick Info */}
              <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-semibold text-gray-900 mb-2">Working Hours:</p>
                <p className="text-sm text-gray-700">{labInfo.workingHours.weekdays}</p>
                <p className="text-sm text-gray-700">{labInfo.workingHours.sunday}</p>
              </div>
            </div>

            <div className="relative">
              <img
                src={heroImages.main}
                alt="Modern Medical Laboratory"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide comprehensive diagnostic services with advanced technology and experienced professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon === 'activity' ? Activity : service.icon === 'scan' ? Scan : HeartPulse;
              return (
                <Card key={service.id} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-0">
                    <div className="h-48 overflow-hidden rounded-t-lg">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {service.description}
                      </p>
                      <Link to="/services" className="text-blue-600 font-semibold flex items-center hover:text-blue-700">
                        Learn More <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={heroImages.equipment}
                alt="Advanced Medical Equipment"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose JKDC Lab?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We are committed to providing accurate diagnostic results with the latest technology and professional care.
              </p>
              <ul className="space-y-4">
                {[
                  'Advanced digital imaging technology',
                  'Experienced and qualified technicians',
                  'Fast and accurate results',
                  'Convenient location with easy access',
                  'Affordable pricing',
                  'Patient-friendly environment'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="inline-block mt-8">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need a Diagnostic Test?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Contact us today to schedule your appointment or visit our lab during working hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${labInfo.phone}`}>
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 w-full sm:w-auto">
                <Phone className="w-5 h-5 mr-2" />
                {labInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-700 w-full sm:w-auto">
                Send Inquiry
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
